export interface PeeqdProfileData {
  username: string;
  displayName: string;
  bio: string;
  postsCount: number;
  followersCount: number;
  followingCount: number;
  mentions: string[];
  externalUrl?: string;
  avatarIndex: number;
  avatarUrl?: string;
  verified?: boolean;
}

function hashSeed(seed: string): number {
  let h = 0;
  for (const c of seed) h = (h + c.charCodeAt(0)) % 10000;
  return h;
}

function titleCaseFromUsername(username: string): string {
  return username
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const PROFILE_OVERRIDES: Record<string, Partial<PeeqdProfileData>> = {
  "otherside.studio": {
    displayName: "Other Side Studio",
    bio: "Independent design studio · brand, motion & digital craft",
    postsCount: 248,
    externalUrl: "otherside.studio",
    avatarIndex: 12,
    verified: false,
  },
  "kyliejenner": {
    displayName: "Kylie",
    postsCount: 7382,
    followersCount: 382_000_000,
    followingCount: 140,
    mentions: ["kyliecosmetics", "khy"],
    externalUrl: "kyliecosmetics.com",
    verified: true,
  },
};

export function formatProfileStat(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return n.toLocaleString("en-US");
}

export function buildProfilePreview(
  rawUsername: string,
  overrides?: Partial<Pick<PeeqdProfileData, "followersCount" | "followingCount" | "postsCount">>
): PeeqdProfileData {
  const username = rawUsername.replace(/^@/, "").toLowerCase();
  const seed = hashSeed(username);
  const preset = PROFILE_OVERRIDES[username] ?? {};

  const followersCount =
    overrides?.followersCount ??
    preset.followersCount ??
    1200 + (seed % 9000);
  const followingCount =
    overrides?.followingCount ??
    preset.followingCount ??
    40 + (seed % 400);
  const postsCount =
    overrides?.postsCount ?? preset.postsCount ?? 80 + (seed % 320);

  const mentions =
    preset.mentions ??
    (seed % 3 === 0 ? [username.split(".")[0] || username] : []);

  return {
    username,
    displayName: preset.displayName ?? titleCaseFromUsername(username),
    bio:
      preset.bio ??
      `${titleCaseFromUsername(username)} on Instagram · tracked by Peeqd`,
    postsCount,
    followersCount,
    followingCount,
    mentions,
    externalUrl: preset.externalUrl ?? (seed % 2 === 0 ? `${username}.com` : undefined),
    avatarIndex: preset.avatarIndex ?? seed % 64,
    verified: preset.verified ?? seed % 11 === 0,
  };
}
